import BaseAutocomplete from './baseComponents/BaseAutocomplete';
import getCookie from './utils/getCookie';

const usPlaceholder = 'city, state, or zip';
const caPlaceholder = 'city, prov or postal';

/**
 * A custom element for enhancing a text box into a near autocomplete widget.
 *
 * @module iabbb-near-autocomplete
 */
class NearAutocomplete extends BaseAutocomplete {
  connectedCallback() {
    this.render();

    const queryParams = new URLSearchParams(window.location.search);

    const searchedLocation = queryParams.get('find_loc');
    const searchedCountry = queryParams.get('find_country');

    if (searchedLocation) {
      this.querySelector('input').value = searchedLocation;

      this.showClearButton();
    }

    if (searchedCountry === 'CAN') {
      this.querySelector('input').placeholder = caPlaceholder;
    }

    if (!searchedLocation) {
      const savedNearLocationId = decodeURIComponent(getCookie('iabbb_find_location'));

      if (savedNearLocationId) {
        const nearLocationIdParts = decodeURIComponent(savedNearLocationId).split('_');
        const nearCountry = nearLocationIdParts[nearLocationIdParts.length - 1];
        const nearState = nearLocationIdParts[nearLocationIdParts.length - 2];
        const nearCity = nearLocationIdParts.slice(0, nearLocationIdParts.length - 2).join(' ');

        if (nearState && nearCity) {
          this.querySelector('input').value = `${nearCity}, ${nearState}`;

          this.showClearButton();
        }

        if (!searchedCountry && nearCountry === 'CAN') {
          this.querySelector('input').placeholder = caPlaceholder;
        }
      }

      self.addEventListener('iabbb-country-custom-select:select', this.changeCountryEventListener);
    }
  }

  getOptionAttributes = (result, inputText) => {
    const raw = result.displayText ?? '';

    // Normalize spacing:
    // 1) ensure space after comma
    // 2) collapse multiple spaces
    // 3) trim
    const normalized = raw
      .replace(/,\s*/g, ', ')
      .replace(/\s{2,}/g, ' ')
      .trim();

    const html =
      inputText.trim().length > 1
        ? normalized.replaceAll(new RegExp(inputText, 'ig'), (match) => `<span>${match}</span>`)
        : normalized;

    return {
      html,
      attributes: result.isCurrentLocation ? [{ name: 'data-value', value: result.value }] : null,
    };
  };

  getOptions = (query) => {
    const userLocation = decodeURIComponent(getCookie('iabbb_user_physical_location'));
    const country = document.querySelector('[name="find_country"]').value;

    let currentLocationOption;

    if (userLocation) {
      const [city, state, userCountry] = userLocation.split(', ');

      if (country === userCountry) {
        currentLocationOption = {
          displayText: 'Current location',
          value: `${city}, ${state}`,
          isCurrentLocation: true,
        };
      }
    }

    if (query.trim().length === 0) {
      return currentLocationOption ? Promise.resolve([currentLocationOption]) : Promise.resolve([]);
    }

    return fetch(
      `/api/suggest/location?country=${country}&input=${query}&maxMatches=10&includePostalCodes=${window.headerFooterData?.includePostalCodesInNearAutocomplete ?? true}`,
    )
      .then((res) => {
        if (res.ok) {
          return res.json();
        }

        return res.text().then((msg) => {
          throw msg;
        });
      })
      .then((results) => (currentLocationOption ? [currentLocationOption, ...results] : results))
      .catch((error) => {
        // biome-ignore lint/suspicious/noConsole: propagate error to Sentry
        console.error(error);
        return Promise.resolve([]);
      });
  };

  changeCountryEventListener = (event) => {
    // Clear out the input from the Near field
    this.querySelector('input').value = '';

    // Update the placeholder to match the country
    this.querySelector('input').placeholder = event.detail.value === 'CAN' ? caPlaceholder : usPlaceholder;
  };
}

customElements.define('iabbb-near-autocomplete', NearAutocomplete);
